<template>
  <div class="audit-toolbar">
    <div class="audit-toolbar-bar">
      <div class="audit-toolbar-search">
        <i class="fa-solid fa-magnifying-glass audit-toolbar-search-icon"></i>
        <input
            v-model="searchText"
            type="text"
            class="form-control audit-toolbar-search-input"
            placeholder="Search ID, voucher no, reference, name, note"
            @keyup.enter="onSearch"
        >
        <button type="button" class="btn btn-primary audit-toolbar-search-btn" @click="onSearch">Search</button>
      </div>
      <div class="audit-toolbar-actions">
        <FilterPopover
            title="Filters"
            :active-count="advancedCount"
            popover-width="560px"
            @open="onOpenFilter"
            @apply="onApplyFilter"
            @clear="onClearAdvanced"
        >
          <div class="col-12 col-sm-6">
            <label class="audit-filter-label">Created By</label>
            <v-select
                v-model="draft.created_by"
                :options="users"
                :reduce="(option) => option.id"
                label="name"
                placeholder="Any user"
            />
          </div>
          <div class="col-12 col-sm-6">
            <label class="audit-filter-label">Updated By</label>
            <v-select
                v-model="draft.updated_by"
                :options="users"
                :reduce="(option) => option.id"
                label="name"
                placeholder="Any user"
            />
          </div>
          <div class="col-12">
            <label class="audit-filter-label">Created Date</label>
            <div class="audit-filter-range">
              <input v-model="draft.created_start" type="date" class="form-control">
              <span class="audit-filter-range-sep">to</span>
              <input v-model="draft.created_end" type="date" class="form-control">
            </div>
          </div>
          <div class="col-12">
            <label class="audit-filter-label">Updated Date</label>
            <div class="audit-filter-range">
              <input v-model="draft.updated_start" type="date" class="form-control">
              <span class="audit-filter-range-sep">to</span>
              <input v-model="draft.updated_end" type="date" class="form-control">
            </div>
          </div>
        </FilterPopover>
        <button type="button" class="btn audit-toolbar-btn" @click="emit('export')">
          <i class="fa-regular fa-file-excel"></i>
          Export
        </button>
      </div>
    </div>
    <div class="audit-toolbar-sub">
      <div class="audit-quick" role="group">
        <button
            v-for="option in quickOptions"
            :key="option.key"
            type="button"
            class="audit-quick-btn"
            :class="{ 'is-active': activeQuick === option.key }"
            @click="onChangeQuick(option.key)"
        >
          {{ option.label }}
        </button>
      </div>
      <div v-if="chips.length" class="audit-chips">
        <span v-for="chip in chips" :key="chip.key" class="audit-chip">
          <span class="audit-chip-label">{{ chip.label }}:</span>
          {{ chip.text }}
          <button type="button" class="audit-chip-remove" @click="emit('remove-filter', chip.fields)">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </span>
        <button type="button" class="audit-chip-clear" @click="onClearAll">Clear Filters</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import { formatToDdMmYy } from '@/services/utils/global'

const props = defineProps({
  filters: {
    type: Object,
    required: true
  },
  users: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['apply', 'clear', 'remove-filter', 'export'])

const advancedFields = ['created_by', 'updated_by', 'created_start', 'created_end', 'updated_start', 'updated_end']

const quickOptions = [
  { key: 'all', label: 'All' },
  { key: 'created', label: 'Created' },
  { key: 'updated', label: 'Updated' }
]

const quickLabels = {
  created: 'Never modified',
  updated: 'Modified'
}

const searchText = ref(props.filters.q || '')
const draft = ref({})

const activeQuick = computed(() => props.filters.quick || 'all')

const advancedCount = computed(() => {
  const userCount = ['created_by', 'updated_by'].filter(field => !! props.filters[field]).length
  const createdRange = props.filters.created_start || props.filters.created_end ? 1 : 0
  const updatedRange = props.filters.updated_start || props.filters.updated_end ? 1 : 0
  return userCount + createdRange + updatedRange
})

const userName = (id) => {
  const user = props.users.find(option => String(option.id) === String(id))
  if (! user) return `#${id}`
  return user.name
}

const rangeText = (start, end) => {
  const from = start ? formatToDdMmYy(start) : 'Beginning'
  const to = end ? formatToDdMmYy(end) : 'Today'
  return `${from} to ${to}`
}

const chips = computed(() => {
  const filters = props.filters
  const list = []
  if (filters.q) list.push({ key: 'q', label: 'Search', text: filters.q, fields: ['q'] })
  if (quickLabels[filters.quick]) list.push({ key: 'quick', label: 'Records', text: quickLabels[filters.quick], fields: ['quick'] })
  if (filters.created_by) list.push({ key: 'created_by', label: 'Created By', text: userName(filters.created_by), fields: ['created_by'] })
  if (filters.updated_by) list.push({ key: 'updated_by', label: 'Updated By', text: userName(filters.updated_by), fields: ['updated_by'] })
  if (filters.created_start || filters.created_end) list.push({ key: 'created', label: 'Created', text: rangeText(filters.created_start, filters.created_end), fields: ['created_start', 'created_end'] })
  if (filters.updated_start || filters.updated_end) list.push({ key: 'updated', label: 'Updated', text: rangeText(filters.updated_start, filters.updated_end), fields: ['updated_start', 'updated_end'] })
  return list
})

const toNumberOrNull = (value) => {
  if (! value) return null
  return Number(value)
}

const onOpenFilter = () => {
  draft.value = {
    created_by: toNumberOrNull(props.filters.created_by),
    updated_by: toNumberOrNull(props.filters.updated_by),
    created_start: props.filters.created_start || '',
    created_end: props.filters.created_end || '',
    updated_start: props.filters.updated_start || '',
    updated_end: props.filters.updated_end || ''
  }
}

const onApplyFilter = () => {
  emit('apply', Object.assign({}, draft.value))
}

const onClearAdvanced = () => {
  emit('remove-filter', advancedFields)
}

const onSearch = () => {
  emit('apply', { q: searchText.value.trim() })
}

const onChangeQuick = (quick) => {
  if (activeQuick.value === quick) return
  emit('apply', { quick: quick === 'all' ? '' : quick })
}

const onClearAll = () => {
  searchText.value = ''
  emit('clear')
}

watch(() => props.filters.q, (value) => {
  searchText.value = value || ''
})
</script>

<style scoped>
.audit-toolbar {
  margin-bottom: 16px;
}
.audit-toolbar-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.audit-toolbar-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 360px;
  max-width: 560px;
}
.audit-toolbar-search-icon {
  position: absolute;
  left: 12px;
  font-size: 13px;
  color: #98a2b3;
  pointer-events: none;
}
.audit-toolbar-search-input {
  padding-left: 34px;
  border-radius: 8px;
  border-color: #e4e7ec;
}
.audit-toolbar-search-btn {
  border-radius: 8px;
  white-space: nowrap;
}
.audit-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.audit-toolbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}
.audit-toolbar-btn:hover {
  background: #f9fafb;
}
.audit-toolbar-sub {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
}
.audit-quick {
  display: inline-flex;
  padding: 3px;
  border-radius: 8px;
  background: #f2f4f7;
}
.audit-quick-btn {
  padding: 5px 14px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #667085;
  cursor: pointer;
}
.audit-quick-btn.is-active {
  background: #fff;
  color: #7367f0;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .08);
}
.audit-chips {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.audit-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px 3px 10px;
  border-radius: 999px;
  background: #f4f3ff;
  font-size: 12px;
  color: #344054;
}
.audit-chip-label {
  font-weight: 600;
  color: #5925dc;
}
.audit-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-size: 11px;
  color: #667085;
}
.audit-chip-remove:hover {
  background: #e9e7fd;
}
.audit-chip-clear {
  border: 0;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #7367f0;
}
.audit-filter-label {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #101828;
}
.audit-filter-range {
  display: flex;
  align-items: center;
  gap: 8px;
}
.audit-filter-range-sep {
  font-size: 12px;
  color: #667085;
}
@media (max-width: 576px) {
  .audit-toolbar-search {
    max-width: none;
  }
  .audit-toolbar-actions {
    width: 100%;
  }
}
</style>
